import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swiper from 'swiper';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Slider from 'components/commercetools-ui/atoms/slider';
import useClassNames from 'helpers/hooks/useClassNames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { classnames } from 'helpers/utils/classnames';
import { desktop } from 'helpers/utils/screensizes';

interface GalleryProps {
  images: Array<ImageProps>;
  inModalVersion?: boolean;
  /** Enable click-to-enlarge modal for gallery images. */
  enableImageModal?: boolean;
  /**
   * Slide to open on. Read once on mount — callers that need to jump an already-mounted
   * gallery should remount it with a new `key`, which is how the PDP restores the viewer's
   * place after the image set changes.
   */
  initialSlide?: number;
  /**
   * Fires only when the viewer moves the gallery — a thumbnail selection or a swipe. It
   * deliberately does NOT fire on mount: a caller that remembers this index would otherwise
   * have its memory overwritten by whatever slide a freshly-mounted gallery happened to
   * start on, including a `initialSlide` the caller derived from an incomplete image set.
   */
  onActiveIndexChange?: (index: number) => void;
  /**
   * Extra UI layered over specific slides, keyed by slide index — e.g. the finish chips on the
   * mosaic's swatch slide. Mark interactive overlays `swiper-no-swiping` so taps don't swipe.
   */
  slideOverlays?: Partial<Record<number, ReactNode>>;
  /** The same, for thumbnails — e.g. a play badge marking the mosaic's video slide. */
  thumbnailOverlays?: Partial<Record<number, ReactNode>>;
}

/**
 * Check if an image has a valid source for display.
 * An image is valid if it has either a src URL or a Cloudinary mediaId.
 */
const hasValidImageSource = (image: ImageProps): boolean => {
  return Boolean(image?.src || image?.media?.mediaId);
};

// Gallery image sizing — full viewport width below desktop, then the main image's share of the
// half-width gallery column (the column is 6/12 and the thumbnail rail takes 116px of it).
const GALLERY_MAX_WIDTH = '560px';
// Mobile gallery spans the viewport; from the md/tablet layout it renders in a 6/12 column.
const GALLERY_IMAGE_SIZES = `(max-width: 743px) 100vw, (max-width: 1023px) 48vw, ${GALLERY_MAX_WIDTH}`;

// Thumbnail sizing — thumbnails use a fixed CSS container size of size-100,
// so fill mode lets the image match that 100px container.
const THUMBNAIL_SIZES = '100px';

// Index at which thumbnails begin lazy-loading. 12 covers two full rows of 6 — the
// maximum visible at 1080p desktop — so the initially-visible set always loads eagerly;
// thumbnails beyond that defer until the user scrolls the rail.
const THUMBNAIL_LAZY_LOAD_INDEX = 12;

// Common props for gallery images
const GALLERY_IMAGE_PROPS = {
  cloudinaryPreset: 'gallery' as const,
  style: { objectFit: 'cover' as const },
  fill: true,
  sizes: GALLERY_IMAGE_SIZES,
};

const Gallery: FC<GalleryProps> = ({
  images,
  inModalVersion,
  enableImageModal = false,
  initialSlide = 0,
  onActiveIndexChange,
  slideOverlays,
  thumbnailOverlays,
}) => {
  const swiperRef = useRef<Swiper>();
  // Clamped on mount because the caller's remembered slide may exceed a shorter image set.
  const [activeSlide, setActiveSlide] = useState(() =>
    Math.min(Math.max(initialSlide, 0), Math.max(images.length - 1, 0)),
  );
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const galleryAreaRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isUserInteraction = useRef(false);
  const [maxThumbnailHeight, setMaxThumbnailHeight] = useState<number | null>(null);

  const showRail = !inModalVersion && images.length > 1;

  const setThumbnailRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      thumbnailRefs.current[index] = el;
    },
    [],
  );

  // The slide Swiper starts on, fixed at mount: it drives which image loads eagerly and
  // which one the pre-Swiper cover shows, neither of which should change as the user swipes.
  const mountedSlide = useRef(activeSlide);

  const [isDesktopSize] = useMediaQuery(desktop);
  const { isTouchDevice } = useTouchDevice();

  const [swiperReady, setSwiperReady] = useState(false);

  const getNormalizedIndex = useCallback(
    (index: number) => {
      const totalSlides = images.length;
      return ((index % totalSlides) + totalSlides) % totalSlides;
    },
    [images.length],
  );

  const slideTo = useCallback(
    (index: number) => {
      if (!swiperRef.current) return;

      isUserInteraction.current = true;
      const normalizedIndex = getNormalizedIndex(index);
      swiperRef.current.slideToLoop(normalizedIndex);
      setActiveSlide(normalizedIndex);
      onActiveIndexChange?.(normalizedIndex);
    },
    [getNormalizedIndex, onActiveIndexChange],
  );

  const handleSlideChange = useCallback(
    (swiper: Swiper) => {
      if (isUserInteraction.current) {
        isUserInteraction.current = false;
        return;
      }

      const newIndex = getNormalizedIndex(swiper.realIndex);
      setActiveSlide(newIndex);
      onActiveIndexChange?.(newIndex);
    },
    [getNormalizedIndex, onActiveIndexChange],
  );

  // Bound the desktop thumbnail rail height to the main image area whenever the rail is shown.
  // Without a finite max-height, the parent CSS grid auto-sizes its row to the taller of (main
  // image, thumbnail rail), so a rail with enough thumbnails to exceed the main image stretches
  // the row and overflows below it. Binding the rail to the main-image height (tracked via
  // ResizeObserver — the main image is aspect-square, so its height follows its column width)
  // keeps the rail from overflowing; the rail's own overflow-y-auto adds a scrollbar only when the
  // thumbnails actually exceed that height. Cleared off-desktop or in a modal (mobile scrolls the
  // rail horizontally instead).
  useEffect(() => {
    if (!isDesktopSize || !showRail) {
      setMaxThumbnailHeight(null);
      return;
    }

    if (typeof ResizeObserver === 'undefined') return;

    const galleryArea = galleryAreaRef.current;
    if (!galleryArea) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setMaxThumbnailHeight(entry.contentRect.height);
    });
    observer.observe(galleryArea);
    return () => observer.disconnect();
  }, [isDesktopSize, showRail]);

  useEffect(() => {
    const container = thumbnailContainerRef.current;
    const activeThumbnail = thumbnailRefs.current[activeSlide];

    if (container && activeThumbnail) {
      const containerRect = container.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();

      if (isDesktopSize) {
        const containerTop = containerRect.top;
        const containerBottom = containerRect.bottom;
        const thumbnailTop = thumbnailRect.top;
        const thumbnailBottom = thumbnailRect.bottom;

        if (thumbnailTop < containerTop || thumbnailBottom > containerBottom) {
          const scrollAmount =
            thumbnailTop - containerTop + container.scrollTop - containerRect.height / 2 + thumbnailRect.height / 2;

          container.scrollTo({
            top: scrollAmount,
            behavior: 'smooth',
          });
        }
      } else {
        const containerLeft = containerRect.left;
        const containerRight = containerRect.right;
        const thumbnailLeft = thumbnailRect.left;
        const thumbnailRight = thumbnailRect.right;

        if (thumbnailLeft < containerLeft || thumbnailRight > containerRight) {
          const scrollAmount =
            thumbnailLeft - containerLeft + container.scrollLeft - containerRect.width / 2 + thumbnailRect.width / 2;

          container.scrollTo({
            left: scrollAmount,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [activeSlide, isDesktopSize]);

  // The frame's rounding and border start at md, where the PDP stops rendering the gallery
  // full-bleed. Below that the image runs edge to edge, and a rounded, bordered box hugging the
  // viewport edge reads as a mistake.
  const containerClassName = useClassNames([
    // col-start-2 is explicit rather than relying on the rail's order-first to push it: with a
    // single image the rail is not rendered, and auto-placement would drop the main image into the
    // 116px rail track and collapse it.
    'relative w-full md:overflow-hidden md:border md:border-stroke-primary lg:col-start-2 lg:row-start-1 lg:self-start',
    inModalVersion ? 'h-[250px]' : 'aspect-square lg:h-auto lg:min-w-0 lg:overflow-hidden',
  ]);
  const scrollbarClass = isTouchDevice ? 'scrollbar-hide' : 'pb-8 lg:pb-0 lg:pr-8';
  const thumbnailScrollClassName = useClassNames([
    'overflow-x-auto scroll-smooth pb-4 lg:h-full lg:overflow-y-auto lg:overflow-x-visible lg:pb-0',
    scrollbarClass,
  ]);

  // 116px = 100px thumbnail tile + ~16px scrollbar gutter. Mac shows overlay scrollbars (no
  // width consumed) but Windows/Linux scrollbars consume ~15-17px of width inside the box.
  // Without the buffer, the tile + scrollbar exceed the column, the overflow cascades up via
  // overflow-x-visible, and a page-level horizontal scrollbar appears on classic-scrollbar OSes.
  const galleryMaxWidth = showRail ? 'min-[1440px]:max-w-[870px]' : 'min-[1440px]:max-w-[740px]';

  const slideImageData = useMemo(
    () =>
      images.map((image) => ({
        src: image?.src || '',
        media: image?.media,
        alt: image?.alt,
      })),
    [images],
  );

  // Memoize to avoid recalculating on every render
  const hasAnyValidImage = useMemo(() => images.some(hasValidImageSource), [images]);

  // [&_.swiper-slide>div] targets Swiper's internal slide wrapper — may need updating if Swiper changes its DOM structure
  return (
    <div
      className={classnames(
        'mx-auto w-full lg:grid lg:[&_.swiper-slide>div]:h-full',
        // 116px = 100px thumbnail + 16px for classic-scrollbar width on Windows/Linux
        // The rail track is reserved even with a single image, leaving that space blank, so the main
        // image is the same size on every product instead of growing to fill the freed column and
        // pushing itself below the fold.
        'lg:grid-cols-[116px_1fr] lg:gap-x-8',
        galleryMaxWidth,
      )}
    >
      <div ref={galleryAreaRef} className={containerClassName}>
        {/* Reserve square space — padding-bottom % is relative to parent width */}
        {!inModalVersion && <div className="pb-[100%]" aria-hidden="true" />}

        {/* Slider — always in the DOM (never display:none) so Swiper initializes with
            correct dimensions. The Slider's own invisible→visible transition handles
            the reveal without layout shift. */}
        <div className={inModalVersion ? 'relative' : 'absolute inset-0'}>
          <Slider
            containerClassName="h-full"
            className="h-full"
            onSlideChange={handleSlideChange}
            onSwiper={(swiper: Swiper) => {
              swiperRef.current = swiper;
            }}
            arrows={isDesktopSize && images?.length > 1}
            innerArrows={true}
            dots={!isDesktopSize && images?.length > 1}
            arrowButtonClasses="h-full"
            compactNavigation={inModalVersion}
            slidesPerView={1}
            initialSlide={mountedSlide.current}
            loop={images?.length > 1}
            onInit={() => setSwiperReady(true)}
            enableModal={enableImageModal && hasAnyValidImage}
            slideImageData={slideImageData}
          >
            {/* Key fallback to index is safe here - images array is stable (from props) and not reordered */}
            {images?.map((image, index) => (
              <div
                key={image?.media?.mediaId || image?.src || index}
                className={classnames('relative w-full', inModalVersion ? 'h-250' : 'h-full')}
              >
                <Image
                  src={image?.src}
                  alt={image?.alt}
                  media={image?.media}
                  loading={index === mountedSlide.current ? 'eager' : 'lazy'}
                  {...GALLERY_IMAGE_PROPS}
                  // A slide may ask Cloudinary for a crop (e.g. the mosaic's square lifestyle photos).
                  ratio={image?.ratio}
                  gravity={image?.gravity}
                  // A slide may ask for a smaller download than a full-width photo (e.g. a texture swatch).
                  sizes={image?.sizes ?? GALLERY_IMAGE_SIZES}
                />
                {slideOverlays?.[index]}
              </div>
            ))}
          </Slider>
        </div>

        {/* Eager cover for the starting slide — visible immediately from SSR, covers the
            gallery until Swiper is ready. Absolutely positioned so it never contributes to
            layout. Tracks the starting slide, not index 0, so restoring a remembered slide
            doesn't flash the first image before Swiper initializes. */}
        {images.length > 0 && (
          <div className={swiperReady ? 'hidden' : 'absolute inset-0 z-10'}>
            <Image
              src={images[mountedSlide.current]?.src}
              alt={images[mountedSlide.current]?.alt}
              media={images[mountedSlide.current]?.media}
              loading="eager"
              {...GALLERY_IMAGE_PROPS}
              // Same crop and size as the slide it stands in for, so the hand-off to Swiper reuses
              // one download instead of showing a soft, uncropped version first.
              ratio={images[mountedSlide.current]?.ratio}
              gravity={images[mountedSlide.current]?.gravity}
              sizes={images[mountedSlide.current]?.sizes ?? GALLERY_IMAGE_SIZES}
            />
          </div>
        )}
      </div>

      {/* px-16 keeps the strip off the viewport edge on mobile (where the main image is full-bleed);
          from md the main image is a bounded box, so dropping the inset lines the strip's scroll
          bounds up with the main image's width. */}
      {showRail && (
        <div className="relative mt-8 flex justify-center px-16 md:justify-start md:px-0 lg:col-start-1 lg:row-start-1 lg:mt-0 lg:block lg:min-h-0 lg:px-0">
          <div
            ref={thumbnailContainerRef}
            className={thumbnailScrollClassName}
            style={maxThumbnailHeight !== null ? { maxHeight: `${maxThumbnailHeight}px` } : undefined}
          >
            <div className="flex min-w-min gap-x-8 lg:min-w-0 lg:flex-col lg:gap-x-0 lg:gap-y-8">
              {/* Key fallback to index is safe - see comment above */}
              {images?.map((image, index) => (
                <div
                  key={image?.media?.mediaId || image?.src || index}
                  ref={setThumbnailRef(index)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View image ${index + 1} of ${images.length}`}
                  aria-pressed={index === activeSlide % images.length}
                  className={classnames(
                    'relative size-100 shrink-0 cursor-pointer border-2 p-1 transition-colors',
                    index === activeSlide % images.length ? 'border-black' : 'border-neutral-black-200',
                  )}
                  onClick={() => slideTo(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      slideTo(index);
                    }
                  }}
                >
                  <div className="relative size-full">
                    <Image
                      src={image?.src}
                      alt={image?.alt}
                      media={image?.media}
                      cloudinaryPreset="thumbnail"
                      style={{ objectFit: 'cover' }}
                      fill
                      sizes={THUMBNAIL_SIZES}
                      loading={index >= THUMBNAIL_LAZY_LOAD_INDEX ? 'lazy' : 'eager'}
                    />
                    {thumbnailOverlays?.[index]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
